/**
 * News Feed Panel - thin ticker at the top (latest item) + "Market News"
 * section below the portfolio (recent 4 items: icon + text + elapsed +
 * direction; tap = coin detail). Leaks render with a RUMOR tag; fact check
 * is an ad-gated verdict reveal.
 */
import { COINS } from '../game/config/coins.js';
import { NEWS_CONFIG, getNewsTemplate } from '../game/config/coinNews.js';
import { getCompany } from '../game/config/companies.js';
import type { FeedItem, LeakItem } from '../game/infoState.js';
import { t, tv } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';
import { coinIconHtml, coinDisplayName } from './coinIcon.js';
import { getState, dispatch, markScreenDirty, requestRender, setViewState, showToast } from './renderer.js';
import { openNewsDetail } from './newsDetailScreen.js';

const shownHooks = new Set<string>();

function coinName(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? coinDisplayName(cfg, getState().exchange.listing[coinId]) : coinId;
}

function coinIconOf(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? coinIconHtml(cfg, true, getState().exchange.listing[coinId]) : '';
}

function elapsedLabel(timestamp: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (sec < 5) return t('news_now');
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m`;
}

function dirArrow(direction: 1 | -1): string {
  return direction === 1
    ? '<span class="nf-dir ex-profit">↑</span>'
    : '<span class="nf-dir ex-loss">↓</span>';
}

function leakText(leak: LeakItem): string {
  const key = `leak_${leak.claimDirection === 1 ? 'up' : 'down'}_${leak.variant}`;
  return tv(key, { coin: coinName(leak.claimCoinId) });
}

/** News headline with {coin} + optional {company} interpolation. */
export function newsText(newsId: string, coinId: string): string {
  const tpl = getNewsTemplate(newsId);
  const co = tpl?.companyId ? getCompany(tpl.companyId) : undefined;
  const vars: Record<string, string> = { coin: coinName(coinId) };
  if (co) vars.company = t(co.nameKey);
  return tv('cnews_' + newsId, vars);
}

/** Company tag badge for company news (empty for regular news). */
export function companyTagHtml(newsId: string): string {
  const tpl = getNewsTemplate(newsId);
  const co = tpl?.companyId ? getCompany(tpl.companyId) : undefined;
  if (!co) return '';
  return `<span class="nf-co-tag" style="border-color:${co.color};color:${co.color}">${t(co.nameKey)}</span>`;
}

function leakBadge(leak: LeakItem): string {
  if (leak.resolved) {
    return leak.genuine
      ? `<span class="nf-badge nf-badge-true">${t('leak_verdict_true')}</span>`
      : `<span class="nf-badge nf-badge-fake">${t('leak_verdict_fake')}</span>`;
  }
  if (leak.factChecked) {
    return leak.genuine
      ? `<span class="nf-badge nf-badge-true">${t('factcheck_true')}</span>`
      : `<span class="nf-badge nf-badge-fake">${t('factcheck_fake')}</span>`;
  }
  return `<span class="nf-badge nf-badge-unknown">?</span>`;
}

function renderLeakRow(leak: LeakItem): string {
  const pending = getState().info.pendingNews;
  let extra = '';
  if (leak.factChecked && !leak.resolved && leak.genuine && pending) {
    // Genuine + fact-checked: confirm the real target coin + direction
    extra = `<div class="nf-confirm">${t('leak_confirmed_target')}: ${coinName(pending.coinId)} ${dirArrow(pending.direction)}</div>`;
  }
  let action = '';
  if (!leak.factChecked && !leak.resolved) {
    if (!shownHooks.has(`fact_check:${leak.leakId}`)) {
      shownHooks.add(`fact_check:${leak.leakId}`);
      dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'fact_check', event: 'shown' } });
    }
    action = `<button class="hook-btn hook-btn-sm" data-factcheck="${leak.leakId}">
      <span class="hook-main">${t('factcheck_btn')}</span>
      <span class="hook-sub">${t('factcheck_sub')}</span>
    </button>`;
  }
  return `<div class="nf-item nf-leak" data-nav-coin="${leak.claimCoinId}">
    <span class="nf-icon">${coinIconOf(leak.claimCoinId)}</span>
    <div class="nf-body">
      <div class="nf-text"><span class="nf-leak-tag">${t('news_leak_tag')}</span> ${leakText(leak)}</div>
      ${extra}
    </div>
    <div class="nf-right">${leakBadge(leak)}<span class="nf-time">${elapsedLabel(leak.createdAt)}</span></div>
    ${action}
  </div>`;
}

function renderNewsRow(item: Extract<FeedItem, { kind: 'news' }>): string {
  const comments = getState().info.playerComments.filter(c => c.instanceId === item.instanceId).length;
  const badge = comments > 0 ? `<span class="nf-cmt-count">${comments}</span>` : '';
  return `<div class="nf-item" data-news-detail="${item.instanceId}">
    <span class="nf-icon">${coinIconOf(item.coinId)}</span>
    <div class="nf-body">
      <div class="nf-text">${companyTagHtml(item.newsId)}${newsText(item.newsId, item.coinId)}</div>
      <div class="nf-cmt-hint">${t('ncm_open_hint')}${badge}</div>
    </div>
    <div class="nf-right">${dirArrow(item.direction)}<span class="nf-time">${elapsedLabel(item.publishedAt)}</span></div>
  </div>`;
}

/** Thin ticker at the top of the exchange screen: latest feed item only. */
export function renderNewsTickerHtml(): string {
  const latest = getState().info.feed[0];
  let ticker: string;
  let nav = '';
  if (!latest) {
    ticker = `<span class="nf-ticker-text nf-ticker-empty">${t('news_ticker_empty')}</span>`;
  } else if (latest.kind === 'leak') {
    nav = ` data-nav-coin="${latest.claimCoinId}"`;
    ticker = `<span class="nf-ticker-text nf-ticker-leak">[${t('news_leak_tag')}] ${leakText(latest)}</span>`;
  } else {
    nav = ` data-nav-coin="${latest.coinId}"`;
    ticker = `<span class="nf-ticker-text">${companyTagHtml(latest.newsId)}${newsText(latest.newsId, latest.coinId)}</span>
      ${dirArrow(latest.direction)}`;
  }
  return `<div class="nf-ticker"${nav}>
    <span class="nf-ticker-label">${t('news_feed_title')}</span>${ticker}
  </div>`;
}

/** "Market News" section (below the portfolio): recent 4 items. */
export function renderNewsSectionHtml(): string {
  const info = getState().info;
  let html = `<div class="ex-section-title">${t('news_section_title')}</div><div class="nf-panel">`;
  if (info.feed.length === 0) {
    html += `<div class="nf-empty">${t('news_ticker_empty')}</div>`;
  }
  for (const item of info.feed.slice(0, NEWS_CONFIG.feedPanelVisible)) {
    html += item.kind === 'leak' ? renderLeakRow(item) : renderNewsRow(item);
  }
  return html + '</div>';
}

async function onFactCheck(leakId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'fact_check', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().FACT_CHECK);
  if (result) {
    dispatch({ type: 'FACTCHECK_LEAK', payload: { leakId } });
    playSfx('achievement');
    const leak = getState().info.feed.find(f => f.kind === 'leak' && f.leakId === leakId);
    if (leak && leak.kind === 'leak') {
      showToast(leak.genuine ? t('factcheck_true') : t('factcheck_fake'));
    }
  }
  markScreenDirty();
  requestRender();
}

/** Bind fact check buttons + coin navigation (call after container HTML changes). */
export function bindNewsFeedEvents(container: HTMLElement) {
  container.querySelectorAll('[data-factcheck]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      void onFactCheck((btn as HTMLElement).dataset.factcheck!);
    });
  });
  container.querySelectorAll('[data-nav-coin]').forEach(row => {
    row.addEventListener('click', () => {
      playSfx('tap');
      setViewState({ currentTab: 'coindetail', selectedCoinId: (row as HTMLElement).dataset.navCoin! });
    });
  });
  container.querySelectorAll('[data-news-detail]').forEach(row => {
    row.addEventListener('click', () => {
      playSfx('tap');
      openNewsDetail((row as HTMLElement).dataset.newsDetail!);
    });
  });
}
